/**
 * Admin-tunable OCR-confidence thresholds (Track C, 2026-05-11).
 *
 * Two sliders control when a cell is rendered red / yellow / green:
 *   • "Rot" — confidence under this value flags the cell as
 *             "Bitte korrigieren". Lower = fewer red flags, more silent
 *             pass-through; higher = more cautious.
 *   • "Grün" — confidence at or above this value is accepted without
 *             review. Lower = less review work, higher risk; higher = more
 *             review work, more safety.
 *
 * A live preview chip-strip below the sliders shows how cells across the
 * confidence spectrum would render at the current threshold values, so
 * admins can dial in the right cost/safety balance interactively.
 */
import { useEffect, useState } from "react";
import { useClientSettings } from "../lib/ClientSettingsContext";
import { cellStatus, STATUS_ICONS, STATUS_LABELS, type CellStatus } from "../lib/cellStatus";

const PREVIEW_CONFIDENCES = [0.15, 0.3, 0.45, 0.6, 0.75, 0.9];

export function SettingsOcrThresholds() {
  const { settings, loaded, setSettingRemote } = useClientSettings();

  // Local draft state so dragging sliders feels responsive without a
  // round-trip per pixel; commit only on mouseup / blur.
  const [draftRed, setDraftRed] = useState(settings.thresholds.red);
  const [draftGreen, setDraftGreen] = useState(settings.thresholds.green);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Sync drafts when the upstream settings change (e.g. after refresh).
  useEffect(() => {
    setDraftRed(settings.thresholds.red);
    setDraftGreen(settings.thresholds.green);
  }, [settings.thresholds.red, settings.thresholds.green]);

  const commitRed = async (value: number) => {
    if (value === settings.thresholds.red) return;
    setError(null);
    setSaving("red");
    try {
      await setSettingRemote("ocr.threshold_red", value);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setDraftRed(settings.thresholds.red);
    } finally {
      setSaving(null);
    }
  };
  const commitGreen = async (value: number) => {
    if (value === settings.thresholds.green) return;
    setError(null);
    setSaving("green");
    try {
      await setSettingRemote("ocr.threshold_green", value);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setDraftGreen(settings.thresholds.green);
    } finally {
      setSaving(null);
    }
  };

  // Keep red < green (else the pruefen window vanishes). Clamp on render.
  const safeRed = Math.min(draftRed, draftGreen - 0.01);
  const safeGreen = Math.max(draftGreen, draftRed + 0.01);

  return (
    <div className="settings-section">
      <h2>OCR-Konfidenz-Schwellen</h2>
      <p className="muted">
        Diese Schwellen bestimmen, ab welcher Konfidenz das System eine Zelle als
        "Bitte korrigieren" (rot) oder "Bitte prüfen" (gelb) markiert.
        Änderungen wirken auf künftige Lesungen; bereits verarbeitete Karten
        behalten ihren bisherigen Status, bis sie neu eingelesen werden.
      </p>

      {!loaded && <p className="muted">Lade Einstellungen…</p>}

      <div className="settings-field">
        <label htmlFor="th-red">
          Rot-Schwelle: <strong>{safeRed.toFixed(2)}</strong>
          <span className="muted"> (Standard: 0.40)</span>
        </label>
        <input
          id="th-red"
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={safeRed}
          onChange={(e) => setDraftRed(parseFloat(e.target.value))}
          onMouseUp={(e) => commitRed(parseFloat((e.target as HTMLInputElement).value))}
          onTouchEnd={(e) => commitRed(parseFloat((e.target as HTMLInputElement).value))}
          disabled={saving === "red"}
        />
        <p className="muted small">
          Werte unter diesem Wert werden rot markiert. Niedriger einstellen =
          weniger rote Zellen.
        </p>
      </div>

      <div className="settings-field">
        <label htmlFor="th-green">
          Grün-Schwelle: <strong>{safeGreen.toFixed(2)}</strong>
          <span className="muted"> (Standard: 0.85)</span>
        </label>
        <input
          id="th-green"
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={safeGreen}
          onChange={(e) => setDraftGreen(parseFloat(e.target.value))}
          onMouseUp={(e) => commitGreen(parseFloat((e.target as HTMLInputElement).value))}
          onTouchEnd={(e) => commitGreen(parseFloat((e.target as HTMLInputElement).value))}
          disabled={saving === "green"}
        />
        <p className="muted small">
          Werte ab diesem Wert werden ohne Prüfung übernommen. Höher einstellen =
          mehr Prüfung, mehr Sicherheit.
        </p>
      </div>

      <div className="settings-preview">
        <strong>Vorschau bei aktueller Einstellung:</strong>
        <div className="preview-chips">
          {PREVIEW_CONFIDENCES.map((c) => {
            const status: CellStatus = cellStatus("1", c, "08:00", {
              red: safeRed,
              green: safeGreen,
            });
            return (
              <span key={c} className={`preview-chip preview-${status}`}>
                {STATUS_ICONS[status]} conf {c.toFixed(2)} · {STATUS_LABELS[status]}
              </span>
            );
          })}
        </div>
      </div>

      {error && <div className="error-banner">Fehler beim Speichern: {error}</div>}
      {saving && <div className="muted small">Speichere…</div>}
    </div>
  );
}
